import { useEffect, useState, useCallback } from 'react'
import HighlightMatch from './HighlightMatch'
import MessageBody from './MessageBody'

export interface Macro {
  id: number
  name: string
  content: string
  usage_count: number
}

interface MacroPickerProps {
  macros: Macro[]
  query: string
  onSelect: (macro: Macro) => void
  onClose: () => void
}

export default function MacroPicker({
  macros,
  query,
  onSelect,
  onClose,
}: MacroPickerProps) {
  const [selectedIndex, setSelectedIndex] = useState(0)

  // Filter macros by query (search in name)
  const filteredMacros = macros.filter((m) =>
    m.name.toLowerCase().includes(query.toLowerCase())
  )

  const selectedMacro = filteredMacros[selectedIndex]

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (filteredMacros.length === 0) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((i) => Math.min(i + 1, filteredMacros.length - 1))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((i) => Math.max(i - 1, 0))
          break
        case 'Enter':
        case 'Tab':
          if (selectedMacro) {
            e.preventDefault()
            onSelect(selectedMacro)
          }
          break
        case 'Escape':
          e.preventDefault()
          onClose()
          break
      }
    },
    [filteredMacros, selectedMacro, onSelect, onClose]
  )

  useEffect(() => {
    // Use capture phase to intercept events before they reach the editor
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [handleKeyDown])

  if (filteredMacros.length === 0) {
    return (
      <div className="absolute bottom-full left-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-64 z-50">
        <p className="text-sm text-gray-500">Nenhum macro encontrado</p>
      </div>
    )
  }

  return (
    <div className="absolute bottom-full left-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg flex w-[560px] h-80 overflow-hidden z-50">
      {/* Left: Macro list */}
      <div className="w-1/2 h-full border-r border-gray-200 overflow-y-auto">
        <div className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-100 sticky top-0 bg-white rounded-tl-lg">
          Mais usados
        </div>
        {filteredMacros.map((macro, index) => (
          <button
            key={macro.id}
            onClick={() => onSelect(macro)}
            onMouseEnter={() => setSelectedIndex(index)}
            className={`w-full text-left px-3 py-2.5 text-sm transition-colors cursor-pointer ${
              index === selectedIndex
                ? 'bg-gray-100'
                : 'hover:bg-gray-50'
            }`}
          >
            <HighlightMatch text={macro.name} query={query} />
          </button>
        ))}
      </div>

      {/* Right: Preview */}
      <div className="w-1/2 h-full p-4 overflow-y-auto">
        {selectedMacro && (
          <>
            <h3 className="font-semibold text-gray-900 mb-3">
              {selectedMacro.name}
            </h3>
            <div className="text-sm text-gray-600 leading-relaxed">
              <MessageBody content={selectedMacro.content} />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
